import React, { useEffect, useState, useReducer, useCallback } from "react";

import {
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    makeStyles,
    InputAdornment,
    TextField,
    CircularProgress
} from "@material-ui/core";

import {
    DeleteOutline,
    Edit,
    Search
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import SpecialtyModal from "../../components/SpecialtyModal";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_SPECIALTIES") {
        const specialties = action.payload;
        const newSpecialties = [];

        specialties.forEach(specialty => {
            const specialtyIndex = state.findIndex(s => s.id === specialty.id);
            if (specialtyIndex !== -1) {
                state[specialtyIndex] = specialty;
            } else {
                newSpecialties.push(specialty);
            }
        });

        return [...state, ...newSpecialties];
    }

    if (action.type === "UPDATE_SPECIALTIES") {
        const specialty = action.payload;
        const specialtyIndex = state.findIndex(s => s.id === specialty.id);

        if (specialtyIndex !== -1) {
            state[specialtyIndex] = specialty;
            return [...state];
        } else {
            return [specialty, ...state];
        }
    }

    if (action.type === "DELETE_SPECIALTY") {
        const specialtyId = action.payload;
        const specialtyIndex = state.findIndex(s => s.id === specialtyId);
        if (specialtyIndex !== -1) {
            state.splice(specialtyIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const Specialties = () => {
    const classes = useStyles();

    const [specialties, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [specialtyModalOpen, setSpecialtyModalOpen] = useState(false);
    const [selectedSpecialty, setSelectedSpecialty] = useState(null);
    const [searchParam, setSearchParam] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingSpecialty, setDeletingSpecialty] = useState(null);

    useEffect(() => {
        dispatch({ type: "RESET" });
    }, [searchParam]);

    const fetchSpecialties = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/specialties", {
                params: { searchParam }
            });
            dispatch({ type: "LOAD_SPECIALTIES", payload: data });
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    }, [searchParam]);

    useEffect(() => {
        fetchSpecialties();
    }, [searchParam, fetchSpecialties]);

    const handleOpenSpecialtyModal = () => {
        setSpecialtyModalOpen(true);
        setSelectedSpecialty(null);
    };

    const handleCloseSpecialtyModal = () => {
        setSpecialtyModalOpen(false);
        setSelectedSpecialty(null);
    };

    const handleEditSpecialty = (specialty) => {
        setSelectedSpecialty(specialty);
        setSpecialtyModalOpen(true);
    };

    const handleDeleteSpecialty = async (specialtyId) => {
        try {
            await api.delete(`/specialties/${specialtyId}`);
            toast.success(i18n.t("specialties.toasts.deleted"));
            dispatch({ type: "DELETE_SPECIALTY", payload: specialtyId });
        } catch (err) {
            toastError(err);
        }
        setDeletingSpecialty(null);
        fetchSpecialties();
    };

    return (
        <MainContainer>
            <SpecialtyModal
                open={specialtyModalOpen}
                onClose={handleCloseSpecialtyModal}
                specialtyId={selectedSpecialty && selectedSpecialty.id}
                reload={fetchSpecialties}
            />
            <ConfirmationModal
                title={deletingSpecialty && `${i18n.t("specialties.confirmationModal.deleteTitle")} ${deletingSpecialty.name}?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteSpecialty(deletingSpecialty.id)}
            >
                {i18n.t("specialties.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <MainHeader>
                <Title>{i18n.t("specialties.title")}</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenSpecialtyModal}
                    >
                        {i18n.t("specialties.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{i18n.t("specialties.table.name")}</TableCell>
                                <TableCell align="center">{i18n.t("specialties.table.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <>
                                {specialties.map((specialty) => (
                                    <TableRow key={specialty.id}>
                                        <TableCell align="center">{specialty.name}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditSpecialty(specialty)}
                                            >
                                                <Edit />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeletingSpecialty(specialty);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loading && <TableRow><TableCell colSpan={2} align="center"><CircularProgress /></TableCell></TableRow>}
                            </>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default Specialties;
